import { getNetworkEnv, type NetworkEnv } from "@/network/env";

const PUBLIC_WALLET_API = "https://testnet.blockchain.wallet.mrbd.ir/api/v1";

function isPrivateApiHost(raw: string): boolean {
  try {
    const u = new URL(raw, window.location.href);
    const h = (u.hostname || "").toLowerCase();
    if (u.protocol === "file:") return true;
    if (!h || h === "localhost" || h.endsWith(".local")) return true;
    if (h === "0.0.0.0" || h === "::1") return true;
    if (/^127\./.test(h) || /^10\./.test(h) || /^192\.168\./.test(h)) return true;
    if (/^172\.(1[6-9]|2\d|3[0-1])\./.test(h)) return true;
    return false;
  } catch {
    return true;
  }
}

export function resolveWalletApi(): string {
  const fromEnv = (import.meta.env.VITE_WALLET_API_BASE || "").trim();
  if (fromEnv && !isPrivateApiHost(fromEnv)) return fromEnv.replace(/\/$/, "");
  try {
    const saved = (localStorage.getItem("nanomWalletApi") || "").trim();
    if (saved && !isPrivateApiHost(saved) && !/nmc-metal/i.test(saved)) {
      return saved.replace(/\/$/, "");
    }
  } catch {
    /* ignore */
  }
  const origin = window.location.origin;
  if (origin && origin !== "null" && !isPrivateApiHost(origin)) {
    return `${origin.replace(/\/$/, "")}/api/v1`;
  }
  return PUBLIC_WALLET_API;
}

export type ApiError = Error & { status?: number; code?: string };

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {},
  sessionToken?: string | null
): Promise<T> {
  const base = resolveWalletApi();
  const headers = new Headers(init.headers || {});
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (sessionToken) headers.set("X-Session-Token", sessionToken);
  const res = await fetch(`${base}${path.startsWith("/") ? path : `/${path}`}`, {
    ...init,
    headers,
  });
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      data = { raw: text };
    }
  }
  if (!res.ok) {
    const detail =
      data && typeof data === "object" && data !== null && "detail" in data
        ? (data as { detail: unknown }).detail
        : data;
    let code = res.statusText;
    let message = `HTTP_${res.status}`;
    if (typeof detail === "string") {
      message = detail;
      code = detail;
    } else if (detail && typeof detail === "object") {
      const d = detail as { code?: unknown; message?: unknown };
      if (d.message != null) message = String(d.message);
      if (d.code != null) code = String(d.code);
      else if (d.message != null) code = String(d.message);
    }
    const err = new Error(message || code || `HTTP_${res.status}`) as ApiError;
    err.status = res.status;
    err.code = code;
    throw err;
  }
  return data as T;
}

export function currentNetworkEnv(): NetworkEnv {
  return getNetworkEnv();
}
